import React, {useState} from "react";
import DialogTitle from "@mui/material/DialogTitle";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import {Button} from "@material-ui/core";
import {ApiDetails, KEY} from "../../../dummyData";
import {CheckCircle as CheckCircleIcon, DeleteOutline, Info as InfoIcon} from "@mui/icons-material";
import CryptoJS from "crypto-js";
import LoadingButton from "@mui/lab/LoadingButton";
import {Box, Paper, Alert, Collapse, Chip} from "@mui/material";
import {CloudUploadOutlined} from "@material-ui/icons";
import {IconFileExcel} from "@tabler/icons-react";

export default function ContentThree(props) {
    const [propertyDetails, setPropertyDetails] = useState("");
    const [filename, setFilename] = useState("");
    const [loading, setLoading] = useState(false);
    const [uploadResults, setUploadResults] = useState(null);
    const [showDetailedResults, setShowDetailedResults] = useState(false);

    const getFileName = (e) => {
        let startIndex =
            e.target.value.indexOf("\\") >= 0
                ? e.target.value.lastIndexOf("\\")
                : e.target.value.lastIndexOf("/");
        let filename = e.target.value.substring(startIndex);
        if (filename.indexOf("\\") === 0 || filename.indexOf("/") === 0) {
            filename = filename.substring(1);
        }
        return filename;
    };

    function handleFile(e) {
        let filename = getFileName(e);
        if (checkTemplateFile(filename)) {
            setFilename(filename);
            setPropertyDetails(e.target.files[0]);
            // Reset previous results when new file is selected
            setUploadResults(null);
            setShowDetailedResults(false);
        } else {
            props.snackBar({
                text: "Please Upload An Excel File (.xlsx)",
                type: "warning",
            });
        }
    }

    function getExtension(filename) {
        let parts = filename.split(".");
        return parts[parts.length - 1];
    }

    function checkTemplateFile(filename) {
        let ext = getExtension(filename);
        return ext.toLowerCase() === "xlsx";
    }

    // Download feedback Excel file
    const downloadFeedbackExcel = (base64Excel, filename) => {
        try {
            // Convert base64 to blob
            const binaryString = window.atob(base64Excel);
            const bytes = new Uint8Array(binaryString.length);
            for (let i = 0; i < binaryString.length; i++) {
                bytes[i] = binaryString.charCodeAt(i);
            }

            const blob = new Blob([bytes], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            });

            // Create download link
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = filename || 'Upload_Results.xlsx';

            // Trigger download
            document.body.appendChild(link);
            link.click();

            // Cleanup
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

        } catch (error) {
            console.error('Error downloading feedback Excel:', error);
            props.snackBar({
                text: "Could not download feedback file, but upload was successful",
                type: "warning",
            });
        }
    };

    // Handle successful upload response
    const handleSuccessfulUpload = (data) => {
        const {
            property_id,
            total_tenants,
            total_units,
            failed_tenants,
            vacant_units,
            feedback_excel,
            feedback_filename,
            tenant_ids
        } = data;

        // Store results for display
        const results = {
            propertyId: property_id,
            totalTenants: total_tenants || 0,
            totalUnits: total_units || 0,
            failedTenants: failed_tenants || 0,
            vacantUnits: vacant_units || 0,
            tenantIds: tenant_ids || [],
            hasFailures: (failed_tenants || 0) > 0,
            hasVacant: (vacant_units || 0) > 0
        };

        setUploadResults(results);

        // Show primary success message
        props.snackBar({
            text: "Account Setup Successful! Property created with detailed processing results.",
            type: "success"
        });

        // Download feedback Excel if available
        if (feedback_excel && feedback_filename) {
            setTimeout(() => {
                downloadFeedbackExcel(feedback_excel, feedback_filename);
                props.snackBar({
                    text: "📋 Detailed upload results downloaded for your review",
                    type: "info",
                    duration: 5000,
                });
            }, 1000);
        }

        setLoading(false);
        setShowDetailedResults(true);

        // Auto-complete after showing results for a few seconds
        setTimeout(() => {
            props.complete();
        }, 3000);
    };

    // Handle upload errors
    const handleUploadError = (data, statusCode) => {
        let errorMessage = "An Error Occurred While Setting Up Your Account.";

        // Handle specific error cases
        if (statusCode === 400) {
            if (data.Message && data.Message.includes("properties_property_name_landlord_id_uindex")) {
                errorMessage = "Property with this name already exists. Please use a different property name.";
            } else if (data.Message && data.Message.includes("Invalid Excel format")) {
                errorMessage = "Invalid Excel format. Please use the provided template.";
            } else if (data.Message && data.Message.includes("Missing required columns")) {
                errorMessage = "Excel file is missing required columns. Please check your template.";
            } else if (data.Message) {
                errorMessage = data.Message;
            }
        } else if (statusCode === 413) {
            errorMessage = "File is too large. Please reduce file size and try again.";
        } else if (statusCode === 500) {
            errorMessage = "Server error occurred. Please contact support - Mark Kieru: 0712292899";
        }

        props.snackBar({
            text: errorMessage,
            type: "error",
        });

        // Also show the fallback message for serious errors
        if (statusCode >= 500) {
            setTimeout(() => {
                props.snackBar({
                    text: "Contact Support For Assistance \nMark Kieru - 0712292899",
                    type: "error"
                });
            }, 2000);
        }

        setLoading(false);
    };

    const uploadFile = async () => {
        if (propertyDetails === "") {
            props.snackBar({
                text: "Please Select File To Upload",
                type: "error",
            });
            return;
        }

        setLoading(true);
        setUploadResults(null);
        setShowDetailedResults(false);

        const formData = new FormData();
        formData.append(
            "details",
            JSON.stringify({
                landlord_id: props.details.get("landlord_id"),
                email_address: props.details.get("email_address"),
            })
        );
        formData.append("updated_document", propertyDetails);

        try {
            const response = await fetch(ApiDetails + "pegasus/visionary/files/finishSetup", {
                method: "POST",
                headers: {
                    "token": "8d5b2e7c-36f4-4b45-b79b-61fc60eb905d"
                },
                body: formData,
            });

            const data = await response.json();

            if (response.status === 200) {
                handleSuccessfulUpload(data);
            } else {
                handleUploadError(data, response.status);
            }
        } catch (error) {
            console.error("Upload error:", error);
            props.snackBar({
                text: "Something went wrong. Please try again later",
                type: "error",
            });
            setLoading(false);
        }
    };

    // Results Summary Component
    const ResultsSummary = ({ results }) => {
        if (!results) return null;

        return (
            <Paper elevation={2} sx={{ p: 3, mt: 3, bgcolor: "#f0f7ff", borderRadius: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <CheckCircleIcon sx={{ color: "#4caf50", mr: 1 }} />
                    <Typography variant="h6" color="primary" fontWeight="medium">
                        Upload Complete!
                    </Typography>
                </Box>

                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
                    <Chip
                        label={`${results.totalTenants} Tenants Created`}
                        color="success"
                        variant="filled"
                        size="small"
                    />
                    <Chip
                        label={`${results.totalUnits} Total Units`}
                        color="primary"
                        variant="outlined"
                        size="small"
                    />
                    {results.hasVacant && (
                        <Chip
                            label={`${results.vacantUnits} Vacant Units`}
                            color="warning"
                            variant="outlined"
                            size="small"
                        />
                    )}
                    {results.hasFailures && (
                        <Chip
                            label={`${results.failedTenants} Failed Records`}
                            color="error"
                            variant="outlined"
                            size="small"
                        />
                    )}
                </Box>

                {results.hasFailures && (
                    <Alert severity="warning" sx={{ mt: 2 }}>
                        <Typography variant="body2">
                            Some records could not be processed. Check the downloaded feedback file for specific reasons and corrections needed.
                        </Typography>
                    </Alert>
                )}

                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                    Property ID: {results.propertyId} • {results.tenantIds.length} tenant accounts created
                </Typography>
            </Paper>
        );
    };

    return (
        <Box sx={{p: 3}}>
            <Typography variant="h5" gutterBottom fontWeight="medium" color="primary">
                Complete Your Setup
            </Typography>

            <Box sx={{mb: 3}}>
                <Typography paragraph>
                    Make sure the changes made to the downloaded document follow the provided guidelines.
                    This document helps the system understand the current status of all your properties.
                </Typography>

                <Paper elevation={1} sx={{p: 3, mb: 3, bgcolor: "#f8f9fa", borderRadius: 2}}>
                    <Typography variant="subtitle1" gutterBottom fontWeight="medium">
                        Upload your edited Excel file:
                    </Typography>

                    {propertyDetails === "" ? (
                        <Box sx={{mt: 2}}>
                            <Button
                                variant="outlined"
                                component="label"
                                startIcon={<CloudUploadOutlined/>}
                                fullWidth
                                sx={{py: 1.5}}
                            >
                                Select Excel File
                                <input type="file" onChange={(e) => handleFile(e)} hidden/>
                            </Button>
                            <Typography variant="caption" color="text.secondary" sx={{mt: 1, display: "block"}}>
                                Only .xlsx files are accepted
                            </Typography>
                        </Box>
                    ) : (
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                p: 2,
                                mt: 2,
                                bgcolor: "#e6f7e6",
                                borderRadius: 1,
                                border: "1px solid #a5d6a7"
                            }}
                        >
                            <Box sx={{display: "flex", alignItems: "center"}}>
                                <IconFileExcel sx={{color: "#2e7d32", mr: 1}}/>
                                <Typography>{filename}</Typography>
                            </Box>
                            <IconButton
                                color="error"
                                size="small"
                                onClick={() => {
                                    setPropertyDetails("");
                                    setFilename(null);
                                    setUploadResults(null);
                                    setShowDetailedResults(false);
                                }}
                            >
                                <DeleteOutline/>
                            </IconButton>
                        </Box>
                    )}
                </Paper>

                {/* Enhanced Features Info */}
                <Alert severity="info" sx={{ mb: 3 }}>
                    <Typography variant="body2">
                        <strong>Enhanced Processing:</strong> The system will automatically validate your data,
                        handle financial records (arrears & wallet balances), send welcome SMS to tenants,
                        and provide a detailed feedback Excel with processing results.
                    </Typography>
                </Alert>
            </Box>

            {/* Show results summary if upload completed */}
            <Collapse in={showDetailedResults}>
                <ResultsSummary results={uploadResults} />
            </Collapse>

            <Box sx={{mt: 3, display: "flex", justifyContent: "center"}}>
                <LoadingButton
                    variant="contained"
                    color="primary"
                    onClick={uploadFile}
                    loading={loading}
                    disabled={propertyDetails === ""}
                    loadingPosition="end"
                    endIcon={<CheckCircleIcon/>}
                    sx={{px: 4, py: 1.2}}
                >
                    {loading ? "Processing Upload..." : "Upload and Complete Setup"}
                </LoadingButton>
            </Box>

            {/* Next Steps - shown after successful upload */}
            {showDetailedResults && uploadResults && (
                <Paper elevation={1} sx={{ p: 3, mt: 3, bgcolor: "#f8f9fa", borderRadius: 2 }}>
                    <Typography variant="subtitle1" gutterBottom fontWeight="medium" color="primary">
                        Next Steps:
                    </Typography>
                    <Box component="ul" sx={{ pl: 2, mb: 0 }}>
                        <li>
                            <Typography variant="body2">
                                Review the feedback Excel file for any failed records
                            </Typography>
                        </li>
                        <li>
                            <Typography variant="body2">
                                Fix any issues and re-upload if needed
                            </Typography>
                        </li>
                        <li>
                            <Typography variant="body2">
                                Your property is now ready for tenant management
                            </Typography>
                        </li>
                        <li>
                            <Typography variant="body2">
                                Tenants will receive welcome SMS with portal access
                            </Typography>
                        </li>
                    </Box>
                </Paper>
            )}
        </Box>
    );
}